import React, { useState, useEffect, useRef } from 'react';
import { get, post, put } from '../../utils/apiClient';
import { clearCacheKey } from '../../utils/cacheUtils';
import Base64Image from '../Base64Image';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminBuildingProjectPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingReason, setUploadingReason] = useState(false);
  const [uploadingFlood, setUploadingFlood] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [newFlood, setNewFlood] = useState({
    caption: '',
    description: '',
    file: null,
    files: [],
  });
  const floodFileInputRef = useRef(null);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/building-project');
        setPageData(data);
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load building project page.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const header = pageData && pageData.header ? pageData.header : null;
  const projectOverview = pageData ? pageData.projectOverview : null;
  const reasonAndImpact = pageData ? pageData.reasonAndImpact : null;
  const floodGallery = pageData && Array.isArray(pageData.floodGallery) ? pageData.floodGallery : [];
  const donationCallToAction = pageData ? pageData.donationCallToAction : null;
  const projectStatus = pageData ? pageData.projectStatus : null;
  const biblicalReference = pageData ? pageData.biblicalReference : null;

  const handleHeaderTitleChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      header: {
        ...((prev && prev.header) || {}),
        title: value,
      },
    }));
  };

  const handleProjectOverviewFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      projectOverview: {
        ...((prev && prev.projectOverview) || {}),
        [field]: field === 'estimatedBudget' && value !== '' ? Number(value) : value === '' ? undefined : value,
      },
    }));
  };

  const handleReasonTextChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      reasonAndImpact: {
        ...((prev && prev.reasonAndImpact) || {}),
        text: value,
      },
    }));
  };

  const handleDonationFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      donationCallToAction: {
        ...((prev && prev.donationCallToAction) || {}),
        [field]: value,
      },
    }));
  };

  const handleStatusFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      projectStatus: {
        ...((prev && prev.projectStatus) || {}),
        [field]: field === 'progressPercentage' && value !== ''
          ? Number(value)
          : value === ''
            ? undefined
            : value,
      },
    }));
  };

  const handleBiblicalFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      biblicalReference: {
        ...((prev && prev.biblicalReference) || {}),
        [field]: value,
      },
    }));
  };

  const updateFloodItem = (index, field, value) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.floodGallery)) return prev;
      const list = prev.floodGallery.map((item, i) => (
        i === index
          ? {
              ...item,
              [field]: value,
            }
          : item
      ));
      return {
        ...prev,
        floodGallery: list,
      };
    });
  };

  const removeFloodItem = (index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.floodGallery)) return prev;
      const list = prev.floodGallery.filter((_, i) => i !== index);
      return {
        ...prev,
        floodGallery: list,
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const payload = {
        header: {
          ...(pageData.header || {}),
          title: (pageData.header && pageData.header.title) || 'Building Project',
        },
        projectOverview: pageData.projectOverview || {},
        reasonAndImpact: pageData.reasonAndImpact || {},
        floodGallery: Array.isArray(pageData.floodGallery) ? pageData.floodGallery : [],
        donationCallToAction: pageData.donationCallToAction || {},
        projectStatus: pageData.projectStatus || {},
        biblicalReference: pageData.biblicalReference || {},
      };

      const updated = await put('/building-project', payload);
      setPageData(updated);
      clearCacheKey('building_project_data');
      setSaveSuccess('Building project page saved successfully.');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save building project page.');
    } finally {
      setSaving(false);
    }
  };

  const handleHeaderBackgroundUpload = async (file) => {
    if (!file) return;
    setUploadingHeader(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await post('/building-project/header/background-image', formData);
      setPageData((prev) => ({
        ...prev,
        header: res.header,
      }));
      clearCacheKey('building_project_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload header background.');
    } finally {
      setUploadingHeader(false);
    }
  };

  const handleReasonImageUpload = async (file) => {
    if (!file) return;
    setUploadingReason(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await post('/building-project/reason-impact/image', formData);
      setPageData((prev) => ({
        ...prev,
        reasonAndImpact: res.reasonAndImpact,
      }));
      clearCacheKey('building_project_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload reason & impact image.');
    } finally {
      setUploadingReason(false);
    }
  };

  const handleNewFloodFieldChange = (field, value) => {
    setNewFlood((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewFloodFileChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setNewFlood((prev) => ({
      ...prev,
      file: files[0] || null,
      files,
    }));
  };

  const handleFloodUpload = async (e) => {
    if (e) e.preventDefault();
    const filesToUpload =
      newFlood.files && newFlood.files.length > 0
        ? newFlood.files
        : newFlood.file
        ? [newFlood.file]
        : [];

    if (filesToUpload.length === 0) {
      setUploadError('Please select at least one image for the flood gallery.');
      return;
    }

    setUploadingFlood(true);
    setUploadError(null);

    try {
      let lastResponse;
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('image', file);
        if (newFlood.caption) formData.append('caption', newFlood.caption);
        if (newFlood.description) formData.append('description', newFlood.description);

        lastResponse = await post('/building-project/flood-gallery/upload', formData);
      }
      
      if (lastResponse && lastResponse.floodGallery) {
        setPageData((prev) => ({
          ...prev,
          floodGallery: lastResponse.floodGallery,
        }));
      }
      
      clearCacheKey('building_project_data');
      setNewFlood({ caption: '', description: '', file: null, files: [] });
      if (floodFileInputRef.current) {
        floodFileInputRef.current.value = '';
      }
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload flood gallery image.');
    } finally {
      setUploadingFlood(false);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>Building Project CMS</AdminSectionTitle>
        <AdminSectionText>Loading building project page...</AdminSectionText>
      </>
    );
  }

  return (
    <>
      <AdminSectionTitle>Building Project CMS</AdminSectionTitle>
      {error && <AdminSectionText style={{ color: '#b71c1c' }}>{error}</AdminSectionText>}
      {uploadError && (
        <AdminSectionText style={{ color: '#b71c1c' }}>{uploadError}</AdminSectionText>
      )}
      {saveSuccess && (
        <AdminSectionText style={{ color: '#1b5e20' }}>{saveSuccess}</AdminSectionText>
      )}

      <form onSubmit={handleSave}>
        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="bp-header-title">Header Title</AdminLoginLabel>
          <AdminLoginInput
            id="bp-header-title"
            value={(header && header.title) || ''}
            onChange={handleHeaderTitleChange}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Project Overview
          </AdminSectionTitle>
          <AdminLoginLabel htmlFor="bp-goal">Goal</AdminLoginLabel>
          <textarea
            id="bp-goal"
            rows={2}
            value={(projectOverview && projectOverview.goal) || ''}
            onChange={(e) => handleProjectOverviewFieldChange('goal', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem', marginBottom: 8 }}
          />
          <AdminLoginLabel htmlFor="bp-description">Description</AdminLoginLabel>
          <textarea
            id="bp-description"
            rows={3}
            value={(projectOverview && projectOverview.description) || ''}
            onChange={(e) => handleProjectOverviewFieldChange('description', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem', marginBottom: 8 }}
          />
          <AdminLoginLabel htmlFor="bp-estimated-budget">Estimated Budget (USD)</AdminLoginLabel>
          <AdminLoginInput
            id="bp-estimated-budget"
            type="number"
            value={
              projectOverview && typeof projectOverview.estimatedBudget === 'number'
                ? projectOverview.estimatedBudget
                : ''
            }
            onChange={(e) => handleProjectOverviewFieldChange('estimatedBudget', e.target.value)}
          />
          <div style={{ marginTop: 8 }}>
            <AdminLoginLabel htmlFor="bp-timeline">Timeline</AdminLoginLabel>
            <AdminLoginInput
              id="bp-timeline"
              value={(projectOverview && projectOverview.timeline) || ''}
              onChange={(e) => handleProjectOverviewFieldChange('timeline', e.target.value)}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Reason &amp; Impact
          </AdminSectionTitle>
          <AdminLoginLabel htmlFor="bp-reason-text">Text</AdminLoginLabel>
          <textarea
            id="bp-reason-text"
            rows={3}
            value={(reasonAndImpact && reasonAndImpact.text) || ''}
            onChange={handleReasonTextChange}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem', marginBottom: 8 }}
          />
          {reasonAndImpact && reasonAndImpact.image && (
            <div style={{ marginBottom: 8 }}>
              <Base64Image
                image={reasonAndImpact.image}
                alt="Reason & impact"
                style={{ width: 200, borderRadius: 6 }}
              />
            </div>
          )}
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="bp-reason-image">Upload Reason &amp; Impact Image</AdminLoginLabel>
            <input
              id="bp-reason-image"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                if (file) {
                  handleReasonImageUpload(file);
                  e.target.value = '';
                }
              }}
              style={{ fontSize: '0.9rem' }}
            />
            {uploadingReason && (
              <AdminSectionText style={{ fontSize: '0.85rem' }}>
                Uploading reason &amp; impact image...
              </AdminSectionText>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Donation Call To Action
          </AdminSectionTitle>
          <AdminLoginLabel htmlFor="bp-donation-title">Title</AdminLoginLabel>
          <AdminLoginInput
            id="bp-donation-title"
            value={(donationCallToAction && donationCallToAction.title) || ''}
            onChange={(e) => handleDonationFieldChange('title', e.target.value)}
          />
          <div style={{ marginTop: 8 }}>
            <AdminLoginLabel htmlFor="bp-donation-message">Message</AdminLoginLabel>
            <textarea
              id="bp-donation-message"
              rows={3}
              value={(donationCallToAction && donationCallToAction.message) || ''}
              onChange={(e) => handleDonationFieldChange('message', e.target.value)}
              style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Project Status
          </AdminSectionTitle>
          <AdminLoginLabel htmlFor="bp-status-text">Status Text</AdminLoginLabel>
          <textarea
            id="bp-status-text"
            rows={2}
            value={(projectStatus && projectStatus.statusText) || ''}
            onChange={(e) => handleStatusFieldChange('statusText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem', marginBottom: 8 }}
          />
          <AdminLoginLabel htmlFor="bp-progress">Progress Percentage</AdminLoginLabel>
          <AdminLoginInput
            id="bp-progress"
            type="number"
            value={
              projectStatus && typeof projectStatus.progressPercentage === 'number'
                ? projectStatus.progressPercentage
                : ''
            }
            onChange={(e) => handleStatusFieldChange('progressPercentage', e.target.value)}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Biblical Reference
          </AdminSectionTitle>
          <AdminLoginLabel htmlFor="bp-bible-ref">Reference</AdminLoginLabel>
          <AdminLoginInput
            id="bp-bible-ref"
            value={(biblicalReference && biblicalReference.reference) || ''}
            onChange={(e) => handleBiblicalFieldChange('reference', e.target.value)}
          />
          <div style={{ marginTop: 8 }}>
            <AdminLoginLabel htmlFor="bp-bible-text">Text</AdminLoginLabel>
            <textarea
              id="bp-bible-text"
              rows={3}
              value={(biblicalReference && biblicalReference.text) || ''}
              onChange={(e) => handleBiblicalFieldChange('text', e.target.value)}
              style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
        <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
          Header Background Image
        </AdminSectionTitle>
        {header && header.backgroundImage && (
          <div style={{ marginBottom: 8 }}>
            <Base64Image
              image={header.backgroundImage}
              alt={header.title || 'Building project header'}
              style={{ width: 260, borderRadius: 6 }}
            />
          </div>
        )}
        <AdminLoginLabel htmlFor="bp-header-bg">Upload Header Background</AdminLoginLabel>
        <input
          id="bp-header-bg"
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
            if (file) {
              handleHeaderBackgroundUpload(file);
              e.target.value = '';
            }
          }}
          style={{ fontSize: '0.9rem' }}
        />
        {uploadingHeader && (
          <AdminSectionText style={{ fontSize: '0.85rem' }}>
            Uploading header background...
          </AdminSectionText>
        )}
      </div>

      <hr style={{ margin: '20px 0' }} />

      <div style={{ marginBottom: 24 }}>
        <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
          Flood Documentation Gallery
        </AdminSectionTitle>
        {floodGallery.length === 0 && (
          <AdminSectionText style={{ fontSize: '0.9rem' }}>
            No flood gallery images added yet.
          </AdminSectionText>
        )}
        {floodGallery.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {floodGallery.map((item, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  padding: 10,
                  marginBottom: 8,
                  fontSize: '0.9rem',
                }}
              >
                {item.image && (
                  <div style={{ marginBottom: 8 }}>
                    <Base64Image
                      image={item.image}
                      alt={item.caption || 'Flood image'}
                      style={{ width: 200, borderRadius: 6 }}
                    />
                  </div>
                )}
                <div style={{ marginBottom: 6 }}>
                  <AdminLoginLabel htmlFor={`flood-caption-${index}`}>
                    Caption
                  </AdminLoginLabel>
                  <AdminLoginInput
                    id={`flood-caption-${index}`}
                    value={item.caption || ''}
                    onChange={(e) => updateFloodItem(index, 'caption', e.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 6 }}>
                  <AdminLoginLabel htmlFor={`flood-description-${index}`}>
                    Description
                  </AdminLoginLabel>
                  <textarea
                    id={`flood-description-${index}`}
                    rows={2}
                    value={item.description || ''}
                    onChange={(e) => updateFloodItem(index, 'description', e.target.value)}
                    style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
                  />
                </div>
                <AdminButton type="button" onClick={() => removeFloodItem(index)}>
                  Remove Entry
                </AdminButton>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 8 }}>
          <AdminSectionTitle style={{ fontSize: '0.95rem', marginBottom: 8 }}>
            Add Flood Gallery Image
          </AdminSectionTitle>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="flood-caption">Caption (optional)</AdminLoginLabel>
            <AdminLoginInput
              id="flood-caption"
              value={newFlood.caption}
              onChange={(e) => handleNewFloodFieldChange('caption', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="flood-description">Description (optional)</AdminLoginLabel>
            <textarea
              id="flood-description"
              rows={2}
              value={newFlood.description}
              onChange={(e) => handleNewFloodFieldChange('description', e.target.value)}
              style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="flood-file">Image File(s)</AdminLoginLabel>
            <input
              id="flood-file"
              type="file"
              accept="image/*"
              multiple
              onChange={handleNewFloodFileChange}
              ref={floodFileInputRef}
              style={{ fontSize: '0.9rem' }}
            />
          </div>
          <AdminLoginButton type="button" onClick={handleFloodUpload} disabled={uploadingFlood}>
            {uploadingFlood ? 'Uploading...' : 'Upload Flood Image'}
          </AdminLoginButton>
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <AdminSaveButton type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Building Project Page'}
        </AdminSaveButton>
      </div>
    </form>
    </>
  );
};

export default AdminBuildingProjectPage;
